import type { SellerOfferDTO } from "@recruiter/shared";

import { isOfferAccepted } from "../../lib/offer";

function money(cents: number, currency: string): string {
  const amount = (cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${currency === "USD" ? "$" : currency + " "}${amount}`;
}

// A price quoted as a RANGE shows BOTH ends back to the talent: `price_cents` is
// only its low end, and reading one end back as "the price" is exactly what the
// client was seeing (Monday 3156054660). Plain hyphen, never a long dash.
function offerPrice(offer: SellerOfferDTO): string {
  const low = money(offer.price_cents, offer.currency);
  const max = offer.price_max_cents;
  return typeof max === "number" && max > offer.price_cents
    ? `${low} - ${money(max, offer.currency)}`
    : low;
}

// The offer STERLING extracted from this thread's negotiation, shown back to the
// talent. Null when no offer has landed yet.
//
// A below-bar offer is still captured/persisted, but it is NOT accepted — STERLING
// pushes back for stronger terms and the client never sees it. So it must read as
// "not sent yet" (amber), never as a done/valid offer (green "Offer captured"),
// which is what `isOfferAccepted` gates on.
export function OfferStatus({ offer }: { offer: SellerOfferDTO | null }) {
  if (!offer) return null;
  const accepted = isOfferAccepted(offer);
  return (
    <div
      className="sl-offer"
      style={
        accepted
          ? undefined
          : {
              borderColor: "var(--rc-color-warning)",
              background: "color-mix(in srgb, var(--rc-color-warning) 10%, transparent)",
            }
      }
    >
      <div
        className="sl-offer__head"
        style={accepted ? undefined : { color: "var(--rc-color-warning)" }}
      >
        {accepted ? "Offer captured" : "Offer not sent"}
      </div>
      {accepted ? null : (
        <div
          style={{
            marginBottom: "var(--rc-space-2)",
            color: "var(--rc-color-text-muted)",
            fontSize: "12px",
          }}
        >
          Sterling asked for stronger terms. The client hasn't seen this offer yet.
        </div>
      )}
      <div className="sl-offer__grid">
        <div>
          <div className="sl-offer__k">Price</div>
          <div className="sl-offer__v">{offerPrice(offer)}</div>
        </div>
        <div>
          <div className="sl-offer__k">Timeline</div>
          {/* timeline_days counts from today: 0 is a same-day delivery, never "0 days". */}
          <div className="sl-offer__v">
            {offer.timeline_days === 0 ? "Same day" : `${offer.timeline_days} days`}
          </div>
        </div>
        <div>
          <div className="sl-offer__k">Revisions</div>
          {/* null = no concrete count stated (Sterling asks for it): show a
              dash, never a raw 0/-1 placeholder. */}
          <div className="sl-offer__v">
            {offer.revision_rounds != null && offer.revision_rounds > 0
              ? offer.revision_rounds
              : "-"}
          </div>
        </div>
      </div>
      {offer.inclusions.length > 0 ? (
        <div style={{ marginTop: "var(--rc-space-2)" }}>
          <div className="sl-offer__k">Includes</div>
          <div>{offer.inclusions.join(", ")}</div>
        </div>
      ) : null}
      {offer.exclusions.length > 0 ? (
        <div style={{ marginTop: "var(--rc-space-2)" }}>
          <div className="sl-offer__k">Excludes</div>
          <div>{offer.exclusions.join(", ")}</div>
        </div>
      ) : null}
    </div>
  );
}
